import $ from 'jquery';
import moment from 'moment';
import React, { useState, useEffect, useCallback } from 'react';

import { requestGet, } from '../../../modules/request/index';
import { PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, } from '../components/progress_reports/progress_report_constants';
import { AppLegend } from '../components/scorebook/app_legend';
import ScoreLegend from '../components/scorebook/score_legend';
import ScorebookFilters from '../components/scorebook/scorebook_filters';
import StudentScores from '../components/scorebook/student_scores';
import EmptyProgressReport from '../components/shared/EmptyProgressReport';
import ArticleSpotlight from '../components/shared/articleSpotlight';
import { ACTIVITY_SUMMARY_FEATURED_BLOG_POST_ID } from '../constants/featuredBlogPost';
import { getPageTitle, Spinner } from '../../Shared/index';

const SCOREBOOK_BEGIN_DATE = 'scorebookBeginDate';
const SCOREBOOK_END_DATE = 'scorebookEndDate';
const SCOREBOOK_DATE_FILTER_NAME = 'scorebookDateFilterName';

interface ClassroomOption {
  id: number;
  name: string;
  value: number | string;
}

interface UnitOption {
  name: string;
  value: number | string;
}

interface DateRangeFilterOption {
  title: string;
  beginDate: moment.Moment | null;
}

interface ScoreData {
  cuId: number;
  activityId: number;
  userId: number;
  updated: string;
  name: string;
  percentage: number;
  timespent: number;
  started: number;
  started_at: string;
  completed_attempts: number;
  marked_complete: boolean;
  activity_description: string;
  activity_classification_id: number;
  dueDate: string;
  publishDate: string;
  unitActivityCreatedAt: string;
  scheduled: boolean;
  locked: boolean;
}

interface StudentScoreData {
  name: string;
  scores: ScoreData[];
}

interface ScorebookProps {
  allClassrooms: ClassroomOption[];
  selectedClassroom?: ClassroomOption;
  premium_state: string;
}

const DATE_RANGE_FILTER_OPTIONS: DateRangeFilterOption[] = [
  {
    title: 'Today',
    beginDate: moment(),
  },
  {
    title: 'This Week',
    beginDate: moment().startOf('week'),
  },
  {
    title: 'This Month',
    beginDate: moment().startOf('month'),
  },
  {
    title: 'Last 7 days',
    beginDate: moment().subtract(7, 'days'),
  },
  {
    title: 'Last 30 days',
    beginDate: moment().subtract(1, 'months'),
  },
  {
    title: 'All Time',
    beginDate: null,
  }
];

// Core Reports -> Activity Summary Report
const Scorebook: React.FC<ScorebookProps> = ({
  allClassrooms,
  selectedClassroom: propsSelectedClassroom,
  premium_state
}) => {

  useEffect(() => {
    document.title = getPageTitle("View Reports", "Activity Summary");
  }, []);

  const allActivityPacksUnit: UnitOption = {
    name: 'All activity packs',
    value: '',
  };

  const [units, setUnits] = useState<UnitOption[]>([]);
  const [classrooms, setClassrooms] = useState<ClassroomOption[]>(allClassrooms);
  const [selectedUnit, setSelectedUnit] = useState<UnitOption>(allActivityPacksUnit);
  const [selectedClassroom, setSelectedClassroom] = useState<ClassroomOption | undefined>(propsSelectedClassroom);
  const [classroomFilters, setClassroomFilters] = useState<ClassroomOption[]>(allClassrooms);
  const [unitFilters, setUnitFilters] = useState<UnitOption[]>([]);
  const [scores, setScores] = useState<Map<number, StudentScoreData>>(new Map());
  const [beginDate, setBeginDate] = useState<moment.Moment | null>(null);
  const [endDate, setEndDate] = useState<moment.Moment | null>(null);
  const [dateFilterName, setDateFilterName] = useState<string | null>(null);
  const [premiumState, setPremiumState] = useState(premium_state);
  const [loading, setLoading] = useState(false);
  const [isLastPage, setIsLastPage] = useState(false);
  const [noLoadHasEverOccurredYet, setNoLoadHasEverOccurredYet] = useState(true);
  const [anyScoresHaveLoadedPreviously, setAnyScoresHaveLoadedPreviously] = useState(
    localStorage.getItem('anyScoresHaveLoadedPreviously') || false
  );
  const [missing, setMissing] = useState<string | undefined>();

  const formatDate = (date: moment.Moment | null) => {
    if (date) {
      const standardizedDate = moment.utc(date);
      return `${standardizedDate.year()}-${standardizedDate.month() + 1}-${standardizedDate.date()}`;
    }
  };

  const convertStoredDateToMoment = (savedString: string | null) => {
    if (savedString && savedString !== 'null') {
      return moment(savedString);
    }
    return null;
  };

  const checkMissing = useCallback((scoresMap: Map<number, StudentScoreData>) => {
    if (!(anyScoresHaveLoadedPreviously == 'true') && scoresMap.size > 0) {
      setAnyScoresHaveLoadedPreviously(true);
      localStorage.setItem('anyScoresHaveLoadedPreviously', 'true');
    }
    if (!classroomFilters || classroomFilters.length === 0) {
      return 'classrooms';
    } else if (anyScoresHaveLoadedPreviously == 'true' && (!scoresMap || scoresMap.size === 0)) {
      return 'activitiesWithinDateRange';
    } else if (unitFilters.length && (!scoresMap || scoresMap.size === 0)) {
      return 'students';
    } else if (!scoresMap || scoresMap.size === 0) {
      return 'activities';
    }
  }, [anyScoresHaveLoadedPreviously, classroomFilters, unitFilters]);

  const displayData = useCallback((data: any) => {
    setClassroomFilters(allClassrooms);
    setIsLastPage(data.is_last_page);
    setPremiumState(data.premium_state);
    setNoLoadHasEverOccurredYet(false);

    setScores(prevScores => {
      const newScores = new Map();
      data.scores.forEach((s: any) => {
        newScores.has(s.user_id) || newScores.set(s.user_id, { name: s.name, scores: [] });
        const scores = newScores.get(s.user_id)!.scores;
        scores.push({
          cuId: s.classroom_unit_id,
          activityId: s.activity_id,
          userId: s.user_id,
          updated: s.updated_at,
          name: s.activity_name,
          percentage: s.percentage,
          timespent: s.timespent,
          started: s.started ? Number(s.started) : 0,
          started_at: s.started_at,
          completed_attempts: s.completed_attempts ? Number(s.completed_attempts) : 0,
          marked_complete: s.marked_complete,
          activity_description: s.activity_description,
          activity_classification_id: s.activity_classification_id,
          dueDate: s.due_date,
          publishDate: s.publish_date,
          unitActivityCreatedAt: s.unit_activity_created_at,
          scheduled: s.scheduled,
          locked: s.locked
        });
      });
      setLoading(false);
      setMissing(checkMissing(newScores));
      return newScores;
    });
  }, [allClassrooms, checkMissing]);

  const fetchData = useCallback(() => {
    if (!selectedClassroom) {
      setMissing('classrooms');
      setLoading(false);
      return;
    }

    setLoading(true);
    $.ajax({
      url: '/teachers/classrooms/scores',
      data: {
        classroom_id: selectedClassroom.value,
        unit_id: selectedUnit.value,
        begin_date: formatDate(beginDate),
        end_date: formatDate(endDate),
        no_load_has_ever_occurred_yet: noLoadHasEverOccurredYet,
      },
      success: displayData,
    });
  }, [selectedClassroom, selectedUnit, beginDate, endDate, noLoadHasEverOccurredYet, displayData]);

  const getUpdatedUnits = useCallback((classroomId: number) => {
    const loadingUnit = { name: 'Loading...', value: '' };
    setUnitFilters([loadingUnit]);
    setSelectedUnit(loadingUnit);

    requestGet(
      `${window.postbuildEnv.DEFAULT_URL}/teachers/classrooms/${classroomId}/units`,
      (body) => {
        const units = body.units;
        if (units.length === 1) {
          const selectedUnit = units[0];
          setUnitFilters(units);
          setSelectedUnit(selectedUnit);
          setMissing(checkMissing(scores));
        } else {
          const selectedUnit = { name: 'All activity packs', value: '' };
          setUnitFilters([selectedUnit].concat(units));
          setSelectedUnit(selectedUnit);
          setMissing(checkMissing(scores));
        }
      }
    );
  }, [scores, checkMissing]);

  const setStateFromLocalStorage = useCallback((callback: () => void) => {
    const storedSelectedClassroomId = window.localStorage.getItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID);
    const storedDateFilterName = window.localStorage.getItem(SCOREBOOK_DATE_FILTER_NAME);
    const dateFilterNameValue = !storedDateFilterName || storedDateFilterName === 'null' ? null : storedDateFilterName;
    const dateRangeFilterOption = DATE_RANGE_FILTER_OPTIONS.find(o => o.title === dateFilterNameValue);

    let newSelectedClassroom: ClassroomOption;

    if (storedSelectedClassroomId) {
      const classroom = classrooms.find(c => Number(c.id) === Number(storedSelectedClassroomId));
      newSelectedClassroom = classroom || allClassrooms[0];
    } else {
      newSelectedClassroom = allClassrooms[0];
    }

    setSelectedClassroom(newSelectedClassroom);

    if (newSelectedClassroom) {
      getUpdatedUnits(newSelectedClassroom.id);
    }

    if (dateRangeFilterOption) {
      const newBeginDate = dateRangeFilterOption.beginDate;
      window.localStorage.setItem(SCOREBOOK_BEGIN_DATE, String(newBeginDate));
      setBeginDate(newBeginDate);
      setDateFilterName(dateFilterNameValue);
      setTimeout(callback, 0);
    } else {
      setBeginDate(convertStoredDateToMoment(window.localStorage.getItem(SCOREBOOK_BEGIN_DATE)));
      setEndDate(convertStoredDateToMoment(window.localStorage.getItem(SCOREBOOK_END_DATE)));
      setTimeout(callback, 0);
    }
  }, [classrooms, allClassrooms, getUpdatedUnits]);

  useEffect(() => {
    if (propsSelectedClassroom) {
      getUpdatedUnits(propsSelectedClassroom.value as number);
    } else {
      setStateFromLocalStorage(fetchData);
    }
  }, []);

  const handleSelectUnit = (option: UnitOption) => {
    setScores(new Map());
    setSelectedUnit(option);
  };

  useEffect(() => {
    if (selectedClassroom && selectedUnit.value !== '') {
      fetchData();
    }
  }, [selectedUnit]);

  const handleSelectClassroom = (option: ClassroomOption) => {
    window.localStorage.setItem(PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, String(option.id));
    getUpdatedUnits(option.value as number);
    setScores(new Map());
    setSelectedClassroom(option);
  };

  useEffect(() => {
    if (selectedClassroom) {
      fetchData();
    }
  }, [selectedClassroom]);

  const handleSelectDates = (newBeginDate: moment.Moment | null, newEndDate: moment.Moment | null, newDateFilterName?: string | null) => {
    window.localStorage.setItem(SCOREBOOK_BEGIN_DATE, String(newBeginDate));
    window.localStorage.setItem(SCOREBOOK_END_DATE, String(newEndDate));
    window.localStorage.setItem(SCOREBOOK_DATE_FILTER_NAME, String(newDateFilterName));
    setScores(new Map());
    setBeginDate(newBeginDate);
    setEndDate(newEndDate);
    setDateFilterName(newDateFilterName || null);
  };

  useEffect(() => {
    if (selectedClassroom) {
      fetchData();
    }
  }, [beginDate, endDate]);

  const scoresForDisplay: JSX.Element[] = [];
  let index = 0;
  scores.forEach((s) => {
    index += 0;
    const sData = s.scores[0];
    scoresForDisplay.push(
      <StudentScores
        data={{
          scores: s.scores,
          name: s.name,
          activity_name: sData.name,
          userId: sData.userId,
          classroomId: selectedClassroom?.id,
        }}
        key={`${sData.userId}`}
        premium_state={premiumState}
      />
    );
  });

  let content;
  if (loading) {
    content = (
      <div>
        {scoresForDisplay}
        <Spinner />
      </div>
    );
  } else if (missing) {
    const onButtonClick = missing == 'activitiesWithinDateRange' ? () => { handleSelectDates(null, null); } : undefined;
    content = <EmptyProgressReport missing={missing} onButtonClick={onButtonClick} />;
  } else {
    content = <div>{scoresForDisplay}</div>;
  }

  return (
    <React.Fragment>
      <main className="page-content-wrapper white-background-accommodate-footer" id="post-secondary-nav">
        <div className="tab-pane" id="scorebook">
          <div className="container">
            <h1>Activity Summary</h1>
            <section aria-label="Filters and Legends" className="section-content-wrapper">
              <ScorebookFilters
                beginDate={beginDate}
                classroomFilters={classroomFilters}
                dateFilterName={dateFilterName}
                dateRangeFilterOptions={DATE_RANGE_FILTER_OPTIONS}
                endDate={endDate}
                selectClassroom={handleSelectClassroom}
                selectDates={handleSelectDates}
                selectedClassroom={selectedClassroom}
                selectedUnit={selectedUnit}
                selectUnit={handleSelectUnit}
                unitFilters={unitFilters}
              />
              <ScoreLegend />
              <AppLegend />
            </section>
            {content}
          </div>
        </div>
      </main>
      <ArticleSpotlight blogPostId={ACTIVITY_SUMMARY_FEATURED_BLOG_POST_ID} />
    </React.Fragment>
  );
};

export default Scorebook;
